import { FORBIDDEN_KEYS } from '../interpolation/forbiddenKeys';
import type { EmbeddedDecisions } from './embeddedDecisions';

/**
 * A definition value as the business rules service returns it: never a
 * formatted string. Money is an integer count of minor units.
 */
export type ValueDetail = {
  readonly value: string | number | boolean;
  readonly type: Readonly<Record<string, unknown>> & { readonly kind: string };
  readonly unit?: string;
  readonly currency?: string;
  readonly version?: number;
};

/**
 * What DSL bindings read under `context.decisions`. Bindings show
 * `values.<definitionId>`, display text built from the service's typed value;
 * `valueDetails.<definitionId>` keeps that typed value for anything that
 * needs the raw number. A variant reads `outcomes` and `statuses`.
 */
export type DecisionsChannel = {
  readonly outcomes: NonNullable<EmbeddedDecisions['outcomes']>;
  readonly statuses: NonNullable<EmbeddedDecisions['statuses']>;
  readonly values: Readonly<Record<string, string>>;
  readonly valueDetails: Readonly<Record<string, ValueDetail>>;
  readonly decisionSet?: EmbeddedDecisions['decisionSet'];
};

/**
 * Display text is formatted with one fixed locale, so the server render and
 * the browser produce the same text and hydration does not mismatch.
 */
const DISPLAY_LOCALE = 'en-US';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isScalar = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' ||
  typeof value === 'boolean' ||
  (typeof value === 'number' && Number.isFinite(value));

/** Own entries of a record, or none for anything else. */
function ownEntries(value: unknown): Array<[string, unknown]> {
  if (!isRecord(value)) return [];
  return Object.keys(value).map((key) => [key, value[key]]);
}

/** Own entries without the prototype names an authored path cannot read. */
function safeEntries(value: unknown): Array<[string, unknown]> {
  return ownEntries(value).filter(([key]) => !FORBIDDEN_KEYS.has(key));
}

/**
 * A frozen record of the entries. Each key becomes an own data property, so
 * a `__proto__` key never reaches the prototype setter.
 */
function freezeRecord<T>(
  entries: Array<[string, T]>,
): Readonly<Record<string, T>> {
  return Object.freeze(Object.fromEntries(entries));
}

/**
 * A copy of an outcome or status entry: one value, or one per variant.
 * Decision ids and variant names are kept whatever they are named; lookups
 * read them as own properties only.
 */
function copyPerVariant(entry: unknown): unknown {
  if (!isRecord(entry)) return entry;
  return freezeRecord(ownEntries(entry));
}

/** ISO 4217 codes this runtime can format, when it can list them. */
function supportedCurrencies(): ReadonlySet<string> | undefined {
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: 'currency') => string[];
  };
  try {
    return intl.supportedValuesOf
      ? new Set(intl.supportedValuesOf('currency'))
      : undefined;
  } catch {
    return undefined;
  }
}

const SUPPORTED_CURRENCIES = supportedCurrencies();

const moneyFormats = new Map<
  string,
  { format: Intl.NumberFormat; digits: number }
>();

/** One formatter per currency, with the minor-unit digits it shows. */
function moneyFormat(currency: string) {
  let entry = moneyFormats.get(currency);
  if (!entry) {
    const format = new Intl.NumberFormat(DISPLAY_LOCALE, {
      style: 'currency',
      currency,
    });
    entry = {
      format,
      digits: format.resolvedOptions().maximumFractionDigits ?? 2,
    };
    moneyFormats.set(currency, entry);
  }
  return entry;
}

function formatMoney(
  minorUnits: unknown,
  currency: unknown,
): string | undefined {
  if (typeof minorUnits !== 'number' || !Number.isSafeInteger(minorUnits)) {
    return undefined;
  }
  if (
    typeof currency !== 'string' ||
    !/^[A-Z]{3}$/.test(currency) ||
    (SUPPORTED_CURRENCIES && !SUPPORTED_CURRENCIES.has(currency))
  ) {
    return undefined;
  }
  const { format, digits } = moneyFormat(currency);
  return format.format(minorUnits / 10 ** digits);
}

/** The most fraction digits a decimal is shown with. */
const MAX_DECIMAL_SCALE = 20;

const DECIMAL_TEXT = /^([+-]?)(\d+)(?:\.(\d+))?$/;

/**
 * A decimal string fixed at `scale` digits, rounding half away from zero,
 * computed on its digits so no precision is lost to a binary conversion.
 */
function formatDecimalText(text: string, scale: number): string | undefined {
  const match = DECIMAL_TEXT.exec(text.trim());
  if (!match) return undefined;
  const [, sign, whole, fraction = ''] = match;
  let digits = BigInt(whole + fraction.padEnd(scale, '0').slice(0, scale));
  if ((fraction[scale] ?? '0') >= '5') digits += BigInt(1);
  const padded = digits.toString().padStart(scale + 1, '0');
  const cut = padded.length - scale;
  const fixed =
    scale > 0 ? `${padded.slice(0, cut)}.${padded.slice(cut)}` : padded;
  return sign === '-' && digits !== BigInt(0) ? `-${fixed}` : fixed;
}

function formatDecimal(value: unknown, scale: unknown): string | undefined {
  const hasScale =
    typeof scale === 'number' && Number.isInteger(scale) && scale >= 0;
  if (typeof value === 'string') {
    if (!hasScale) {
      return DECIMAL_TEXT.test(value.trim()) ? value.trim() : undefined;
    }
    return formatDecimalText(value, Math.min(scale, MAX_DECIMAL_SCALE));
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  if (!hasScale) return String(value);
  return value.toFixed(Math.min(scale, MAX_DECIMAL_SCALE));
}

/**
 * The text a binding shows for one definition value, or `undefined` when
 * there is nothing to show: a null value, a value the service could not
 * compute, or a malformed entry. A bare scalar a host passed is shown as is.
 *
 * - `money`: the integer minor-unit amount in its currency, `45999` USD as
 *   `$459.99`
 * - `decimal`: fixed at its scale, `5.2` at scale 2 as `5.20`
 * - `integer`, `string`, `enum`, `date`, `boolean`: the value as sent, with
 *   no grouping, so a year stays `2024`
 * - any other kind, or none: nothing
 *
 * A unit is not appended; authors write it in their copy.
 */
export function displayDefinitionValue(entry: unknown): string | undefined {
  if (isScalar(entry)) return String(entry);
  if (!isRecord(entry) || !isRecord(entry.type)) return undefined;
  const { value, type } = entry;
  if (!isScalar(value)) return undefined;
  switch (type.kind) {
    case 'money':
      return formatMoney(value, entry.currency ?? type.currency);
    case 'decimal':
      return formatDecimal(value, type.scale);
    case 'integer':
    case 'string':
    case 'enum':
    case 'date':
    case 'boolean':
      return String(value);
    default:
      return undefined;
  }
}

/** A frozen copy of a value's type, nested lists and records included. */
function copyValueType(type: Record<string, unknown>): ValueDetail['type'] {
  return freezeRecord(
    safeEntries(type).map(([key, field]) => [key, copyTypeField(field)]),
  ) as ValueDetail['type'];
}

function copyTypeField(field: unknown): unknown {
  if (Array.isArray(field)) return Object.freeze(field.map(copyTypeField));
  if (isRecord(field)) {
    return freezeRecord(
      safeEntries(field).map(([key, nested]) => [key, copyTypeField(nested)]),
    );
  }
  return field;
}

function readValueDetail(entry: unknown): ValueDetail | undefined {
  if (!isRecord(entry) || !isRecord(entry.type)) return undefined;
  if (typeof entry.type.kind !== 'string' || !isScalar(entry.value)) {
    return undefined;
  }
  return Object.freeze({
    value: entry.value,
    type: copyValueType(entry.type),
    ...(typeof entry.unit === 'string' ? { unit: entry.unit } : {}),
    ...(typeof entry.currency === 'string' ? { currency: entry.currency } : {}),
    ...(typeof entry.version === 'number' ? { version: entry.version } : {}),
  });
}

function readDecisionSet(value: unknown): DecisionsChannel['decisionSet'] {
  if (
    isRecord(value) &&
    typeof value.decisionSetId === 'string' &&
    typeof value.version === 'number'
  ) {
    return Object.freeze({
      decisionSetId: value.decisionSetId,
      version: value.version,
    });
  }
  return undefined;
}

export const EMPTY_DECISIONS_CHANNEL: DecisionsChannel = Object.freeze({
  outcomes: Object.freeze({}),
  statuses: Object.freeze({}),
  values: Object.freeze({}),
  valueDetails: Object.freeze({}),
});

/**
 * Builds the read-only `context.decisions` entry from the rule results the
 * page host passed. Every section is copied and frozen, so neither authored
 * config nor a component can change a result after it arrives.
 */
export function toDecisionsChannel(embedded: unknown): DecisionsChannel {
  if (!isRecord(embedded)) return EMPTY_DECISIONS_CHANNEL;
  const values: Array<[string, string]> = [];
  const valueDetails: Array<[string, ValueDetail]> = [];
  for (const [id, entry] of safeEntries(embedded.values)) {
    const text = displayDefinitionValue(entry);
    if (text !== undefined) values.push([id, text]);
    const detail = readValueDetail(entry);
    if (detail) valueDetails.push([id, detail]);
  }
  const decisionSet = readDecisionSet(embedded.decisionSet);
  return Object.freeze({
    outcomes: freezeRecord(
      ownEntries(embedded.outcomes).map(([id, entry]) => [
        id,
        copyPerVariant(entry),
      ]),
    ) as DecisionsChannel['outcomes'],
    statuses: freezeRecord(
      ownEntries(embedded.statuses).map(([id, entry]) => [
        id,
        copyPerVariant(entry),
      ]),
    ) as DecisionsChannel['statuses'],
    values: freezeRecord(values),
    valueDetails: freezeRecord(valueDetails),
    ...(decisionSet ? { decisionSet } : {}),
  });
}
